import { useCallback, useEffect, useMemo, useState } from "react";

import { connectTelemetry, mergeSnapshot, type TelemetrySnapshot } from "./api/telemetry.js";
import type { AlarmRow } from "./components/AlarmBanner.js";
import { resolveStatus, type Cluster } from "./components/ClusterCard.js";
import { Shell, type View } from "./components/Shell.js";
import { Emissions } from "./screens/Emissions.js";
import { FocusMode } from "./screens/FocusMode.js";
import { CalibrationWizard } from "./screens/CalibrationWizard.js";
import { Home } from "./screens/Home.js";
import { LogsPage } from "./screens/LogsPage.js";
import { Settings } from "./screens/Settings.js";
import { Users } from "./screens/Users.js";
import { useI18n } from "./i18n.js";
import { useSession } from "./session.js";

/** Stato hardware per cluster, da `hardware-probe.results`. */
export type ProbeResults = Record<
  string,
  { name: string; camera: string; sensor: string; ptz: string }
>;

/** Il probe sul server gira ogni 10 s: chiederlo piu' spesso non produce dati
 *  nuovi, solo traffico. */
const PROBE_POLL_MS = 10_000;
/** Gli allarmi si rileggono ogni 5 s, come nell'app desktop. */
const ALARM_POLL_MS = 5_000;

/**
 * Quello che sta dentro la sessione, e come ci si muove.
 *
 * La navigazione ricalca l'app desktop e non e' una tabella di schede:
 *
 *  - la **griglia degli impianti** e' il posto dove si sta;
 *  - **emissioni** e **configurazione** si aprono dalle icone in alto a
 *    destra e tornano indietro;
 *  - **impianto singolo**, **registro eventi** e **gestione utenti** sono
 *    schermate che si spingono sopra, con la freccia per tornare — nel `.qml`
 *    sono `stack.push()`.
 *
 * Telemetria, stato hardware e allarmi vivono QUI, non nelle singole
 * schermate: la connessione deve sopravvivere al cambio di vista. Tenerli
 * dentro la Home significava chiuderli e riaprirli ogni volta, e ogni
 * riapertura e' una finestra in cui la sorveglianza non arriva.
 */
export function Workspace() {
  const { api, logout } = useSession();
  const { t } = useI18n();

  const [view, setView] = useState<View>("home");
  /** L'impianto aperto in grande. `undefined` = si e' sulla griglia. */
  const [focus, setFocus] = useState<Cluster | undefined>();
  /** Le schermate spinte sopra tutto: registro e utenti. */
  const [overlay, setOverlay] = useState<"logs" | "users" | "calibration" | undefined>();
  /** L'impianto su cui si sta calibrando la planimetria. */
  const [calibrating, setCalibrating] = useState<Cluster | undefined>();
  /** Griglia o tabella: sta qui perche' il selettore e' nella barra in alto. */
  const [mode, setMode] = useState<"grid" | "sitemap">("grid");

  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [telemetry, setTelemetry] = useState<TelemetrySnapshot>({});
  const [probe, setProbe] = useState<ProbeResults>({});
  const [alarms, setAlarms] = useState<AlarmRow[]>([]);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const conn = connectTelemetry({
      onUpdate: (patch) => setTelemetry((cur) => mergeSnapshot(cur, patch)),
      onConnectedChange: setLive,
      // Il WebSocket rifiutato e' l'unico segnale che la sessione e' caduta
      // mentre la pagina era aperta senza fare chiamate.
      onUnauthenticated: () => void logout(),
    });
    return () => conn.close();
  }, [logout]);

  useEffect(() => {
    void api
      .call<Cluster[]>("clusters.list")
      .then(setClusters)
      .catch(() => {
        /* la griglia lo dice da sola */
      });
  }, [api]);

  useEffect(() => {
    let stop = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const tick = async () => {
      try {
        const r = await api.call<ProbeResults>("hardware-probe.results");
        if (!stop) setProbe(r);
      } catch {
        // Il probe che non risponde non svuota la schermata: si tiene
        // l'ultimo stato noto e si riprova.
      }
      if (!stop) timer = setTimeout(() => void tick(), PROBE_POLL_MS);
    };
    void tick();
    return () => {
      stop = true;
      if (timer) clearTimeout(timer);
    };
  }, [api]);

  const reloadAlarms = useCallback(() => {
    void api
      .call<AlarmRow[]>("alarms.list")
      .then(setAlarms)
      .catch(() => {
        /* niente allarmi leggibili: la campanella resta spenta */
      });
  }, [api]);

  useEffect(() => {
    reloadAlarms();
    const timer = setInterval(reloadAlarms, ALARM_POLL_MS);
    return () => clearInterval(timer);
  }, [reloadAlarms]);

  const critical = useMemo(
    () =>
      clusters.filter((c) => resolveStatus(c, telemetry[c.id], probe[c.id]) === "CRITICAL").length,
    [clusters, telemetry, probe],
  );

  const clusterNames = useMemo(
    () => Object.fromEntries(clusters.map((c) => [c.id, c.name])),
    [clusters],
  );

  // Uscire dall'impianto chiude anche il video: tornare alle emissioni e
  // ritrovarsi un decodificatore acceso sotto sarebbe banda per niente.
  const goto = (v: View) => {
    setFocus(undefined);
    setOverlay(undefined);
    setView(v);
  };

  const shell = (content: React.ReactNode, center?: React.ReactNode, bare?: boolean) => (
    <Shell
      view={view}
      onView={goto}
      live={live}
      criticalCount={critical}
      alarms={alarms}
      clusterNames={clusterNames}
      onAlarmsChanged={reloadAlarms}
      center={center}
      {...(bare ? { bare: true } : {})}
    >
      {content}
    </Shell>
  );

  if (overlay === "calibration" && calibrating) {
    return shell(
      <CalibrationWizard cluster={calibrating} onBack={() => setOverlay(undefined)} />,
    );
  }

  if (overlay === "users") {
    return shell(<Users onBack={() => setOverlay(undefined)} />);
  }

  if (overlay === "logs" && focus) {
    return shell(<LogsPage cluster={focus} onBack={() => setOverlay(undefined)} />);
  }

  if (focus) {
    return shell(
      <FocusMode
        cluster={focus}
        telemetry={telemetry[focus.id]}
        probe={probe[focus.id]}
        alarms={alarms}
        onAlarmsChanged={reloadAlarms}
        onBack={() => setFocus(undefined)}
        onLogs={() => setOverlay("logs")}
      />,
      undefined,
      // L'impianto singolo occupa tutta la finestra, come nel .qml: si esce
      // con la freccia in alto a sinistra, non dalla barra.
      true,
    );
  }

  const toggle = (
    <div className="home__toggle">
      <button
        className={`home__mode${mode === "grid" ? " home__mode--on" : ""}`}
        onClick={() => setMode("grid")}
      >
        {t("nav.gridView")}
      </button>
      <button
        className={`home__mode${mode === "sitemap" ? " home__mode--on" : ""}`}
        onClick={() => setMode("sitemap")}
      >
        {t("nav.siteMap")}
      </button>
    </div>
  );

  if (view === "emissions") return shell(<Emissions onBack={() => setView("home")} />);

  if (view === "settings") {
    return shell(
      <Settings
        onManageUsers={() => setOverlay("users")}
        onCalibrate={(c) => {
          setCalibrating(c);
          setOverlay("calibration");
        }}
        telemetry={telemetry}
        onOpenCluster={(c) => {
          setView("home");
          setFocus(c);
        }}
        onBack={() => setView("home")}
      />,
    );
  }

  return shell(
    <Home
      clusters={clusters}
      mode={mode}
      telemetry={telemetry}
      probe={probe}
      onOpen={setFocus}
    />,
    toggle,
  );
}
